import { ChevronDown, ChevronRight, Hash, Layers, Table2 } from 'lucide-react';
import { useState } from 'react';
import { useSchemaStore } from '../../store/useSchemaStore';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="mb-1">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-1 px-2 py-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
      >
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        {title}
      </button>
      {open && <div className="pb-1">{children}</div>}
    </div>
  );
}

export default function SchemaExplorer({ width }: { width: number }) {
  const schema = useSchemaStore((s) => s.schema);
  const selection = useSchemaStore((s) => s.selection);
  const setSelection = useSchemaStore((s) => s.setSelection);
  const addTable = useSchemaStore((s) => s.addTable);
  const addGroup = useSchemaStore((s) => s.addGroup);
  const [filter, setFilter] = useState('');

  const tables = schema.tables.filter((t) => t.name.toLowerCase().includes(filter.toLowerCase()));

  return (
    <div
      style={{ width }}
      className="flex h-full shrink-0 flex-col border-r border-gray-200/70 bg-panel dark:border-white/10 dark:bg-gray-900"
    >
      <div className="border-b border-gray-200/70 p-2 dark:border-white/10">
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter schema..."
          className="w-full rounded-lg border border-gray-200 px-2 py-1 text-sm focus:border-sky-400 focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500"
        />
      </div>
      <div className="flex-1 overflow-y-auto px-1 py-2">
        <Section title={`Tables (${tables.length})`}>
          {tables.map((table) => (
            <button
              key={table.id}
              onClick={() => setSelection({ type: 'table', id: table.id })}
              className={`flex w-full items-center gap-2 rounded px-2 py-1 text-left text-sm ${
                selection?.type === 'table' && selection.id === table.id
                  ? 'bg-sky-50 text-sky-700 dark:bg-sky-500/20 dark:text-sky-300'
                  : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800'
              }`}
            >
              <Table2 className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{table.name}</span>
              <span className="ml-auto text-xs text-gray-400 dark:text-gray-500">{table.columns.length}</span>
            </button>
          ))}
          <button
            onClick={() => addTable()}
            className="mt-1 flex w-full items-center gap-2 rounded px-2 py-1 text-left text-sm text-sky-600 hover:bg-sky-50 dark:text-sky-400 dark:hover:bg-sky-500/10"
          >
            + Add table
          </button>
        </Section>

        <Section title={`Groups (${schema.groups.length})`}>
          {schema.groups.map((group) => (
            <button
              key={group.id}
              onClick={() => setSelection({ type: 'group', id: group.id })}
              className={`flex w-full items-center gap-2 rounded px-2 py-1 text-left text-sm ${
                selection?.type === 'group' && selection.id === group.id
                  ? 'bg-sky-50 text-sky-700 dark:bg-sky-500/20 dark:text-sky-300'
                  : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800'
              }`}
            >
              <Layers className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{group.name}</span>
              <span className="ml-auto text-xs text-gray-400 dark:text-gray-500">{group.tableIds.length}</span>
            </button>
          ))}
          <button
            onClick={() => addGroup()}
            className="mt-1 flex w-full items-center gap-2 rounded px-2 py-1 text-left text-sm text-sky-600 hover:bg-sky-50 dark:text-sky-400 dark:hover:bg-sky-500/10"
          >
            + Add group
          </button>
        </Section>

        <Section title={`Enums (${schema.enums.length})`}>
          {schema.enums.map((en) => (
            <button
              key={en.id}
              onClick={() => setSelection({ type: 'enum', id: en.id })}
              className={`flex w-full items-center gap-2 rounded px-2 py-1 text-left text-sm ${
                selection?.type === 'enum' && selection.id === en.id
                  ? 'bg-sky-50 text-sky-700 dark:bg-sky-500/20 dark:text-sky-300'
                  : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800'
              }`}
            >
              <Hash className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{en.name}</span>
              <span className="ml-auto text-xs text-gray-400 dark:text-gray-500">{en.values.length}</span>
            </button>
          ))}
        </Section>
      </div>
    </div>
  );
}
